import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Field';
import { Banner } from '@/components/ui/Feedback';
import { useAuth } from '@/features/auth/AuthContext';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { IndicatorEditor } from './IndicatorEditor';
import {
  instrumentApi,
  type Dimension,
  type Indicator,
  type IndicatorInput,
  type InstrumentDetail,
  type Section,
} from './api';

type EditTarget =
  | { kind: 'indicator'; dimensionId: string; indicator: Indicator | null; nextNumber: number }
  | { kind: 'dimension'; sectionId: string; dimension: Dimension | null }
  | null;

type DeleteTarget =
  | { kind: 'indicator'; id: string; label: string }
  | { kind: 'dimension'; id: string; label: string; indicatorCount: number }
  | null;

export function StructureTab({ data }: { data: InstrumentDetail }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = user?.role === 'super_admin';

  const [editing, setEditing] = useState<EditTarget>(null);
  const [deleting, setDeleting] = useState<DeleteTarget>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['instrument', data.id] });
    queryClient.invalidateQueries({ queryKey: ['instruments'] });
  };

  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Gagal menyimpan perubahan.');

  const saveIndicator = useMutation({
    mutationFn: (input: IndicatorInput) => {
      if (editing?.kind !== 'indicator') throw new Error('no target');
      return editing.indicator
        ? instrumentApi.updateIndicator(editing.indicator.id, input)
        : instrumentApi.createIndicator(editing.dimensionId, input);
    },
    onSuccess: () => {
      refresh();
      setEditing(null);
      setError(null);
      setBanner('Indikator tersimpan.');
    },
    onError: fail,
  });

  const saveDimension = useMutation({
    mutationFn: (input: { code: string; name: string }) => {
      if (editing?.kind !== 'dimension') throw new Error('no target');
      return editing.dimension
        ? instrumentApi.updateDimension(editing.dimension.id, input)
        : instrumentApi.createDimension(editing.sectionId, input);
    },
    onSuccess: () => {
      refresh();
      setEditing(null);
      setError(null);
      setBanner('Dimensi tersimpan.');
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!deleting) throw new Error('no target');
      return deleting.kind === 'indicator'
        ? instrumentApi.deleteIndicator(deleting.id)
        : instrumentApi.deleteDimension(deleting.id);
    },
    onSuccess: () => {
      refresh();
      setDeleting(null);
      setBanner('Data dihapus.');
    },
    onError: (err) => {
      setDeleting(null);
      fail(err);
    },
  });

  return (
    <div className="flex flex-col gap-4">
      {banner && <Banner tone="success">{banner}</Banner>}
      {error && !editing && <Banner tone="danger">{error}</Banner>}

      {data.sections.map((section) => (
        <SectionEditor
          key={section.id}
          section={section}
          canEdit={canEdit}
          showHeader={data.sections.length > 1}
          evidenceLabel={data.version.evidenceLabel ?? 'Bukti'}
          onAddDimension={() => setEditing({ kind: 'dimension', sectionId: section.id, dimension: null })}
          onEditDimension={(dimension) =>
            setEditing({ kind: 'dimension', sectionId: section.id, dimension })
          }
          onDeleteDimension={(dimension) =>
            setDeleting({
              kind: 'dimension',
              id: dimension.id,
              label: `${dimension.code} — ${dimension.name}`,
              indicatorCount: dimension.indicators.length,
            })
          }
          onAddIndicator={(dimension) =>
            setEditing({
              kind: 'indicator',
              dimensionId: dimension.id,
              indicator: null,
              nextNumber: nextNumberFor(data),
            })
          }
          onEditIndicator={(dimension, indicator) =>
            setEditing({
              kind: 'indicator',
              dimensionId: dimension.id,
              indicator,
              nextNumber: indicator.number,
            })
          }
          onDeleteIndicator={(indicator) =>
            setDeleting({
              kind: 'indicator',
              id: indicator.id,
              label: `${indicator.number}. ${indicator.name}`,
            })
          }
        />
      ))}

      {editing?.kind === 'indicator' && (
        <IndicatorEditor
          indicator={editing.indicator}
          scaleLabels={data.version.scaleLabels}
          evidenceLabel={data.version.evidenceLabel ?? 'Bukti'}
          nextNumber={editing.nextNumber}
          saving={saveIndicator.isPending}
          error={error}
          onCancel={() => {
            setEditing(null);
            setError(null);
          }}
          onSave={(input) => saveIndicator.mutate(input)}
        />
      )}

      {editing?.kind === 'dimension' && (
        <DimensionEditor
          dimension={editing.dimension}
          saving={saveDimension.isPending}
          error={error}
          onCancel={() => {
            setEditing(null);
            setError(null);
          }}
          onSave={(input) => saveDimension.mutate(input)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={deleting?.kind === 'dimension' ? 'Hapus dimensi?' : 'Hapus indikator?'}
        confirmLabel="Ya, hapus"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
      >
        <p className="mb-3 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 font-medium text-ink">
          {deleting?.label}
        </p>
        {deleting?.kind === 'dimension' && deleting.indicatorCount > 0 && (
          <p className="mb-3 rounded-[var(--radius-input)] bg-danger-soft px-3.5 py-2.5 text-danger">
            <strong>{deleting.indicatorCount} indikator</strong> di dalamnya ikut terhapus, beserta
            seluruh kriteria dan bukti masing-masing.
          </p>
        )}
        <p>
          Bobot yang hilang membuat Σ tidak lagi 100 — sisanya perlu dibagikan ulang ke indikator
          lain agar indeks tetap bermakna.
        </p>
      </ConfirmDialog>
    </div>
  );
}

/** Nomor indikator berikutnya, unik di seluruh instrumen (bukan per dimensi). */
function nextNumberFor(data: InstrumentDetail): number {
  const numbers = data.sections.flatMap((s) =>
    s.dimensions.flatMap((d) => d.indicators.map((i) => i.number)),
  );
  return numbers.length === 0 ? 1 : Math.max(...numbers) + 1;
}

function SectionEditor({
  section,
  canEdit,
  showHeader,
  evidenceLabel,
  onAddDimension,
  onEditDimension,
  onDeleteDimension,
  onAddIndicator,
  onEditIndicator,
  onDeleteIndicator,
}: {
  section: Section;
  canEdit: boolean;
  showHeader: boolean;
  evidenceLabel: string;
  onAddDimension: () => void;
  onEditDimension: (d: Dimension) => void;
  onDeleteDimension: (d: Dimension) => void;
  onAddIndicator: (d: Dimension) => void;
  onEditIndicator: (d: Dimension, i: Indicator) => void;
  onDeleteIndicator: (i: Indicator) => void;
}) {
  // Bobot dihitung ulang di layar dari indikator yang sedang tampil, bukan
  // diambil dari server — begitu satu bobot diubah, angkanya ikut berubah
  // tanpa menunggu muat ulang, dan penjaga Σ=100 selalu jujur.
  const total = section.dimensions.reduce(
    (sum, d) => sum + d.indicators.reduce((s, i) => s + i.weight, 0),
    0,
  );
  const balanced = Math.abs(total - 100) < 1e-9;

  return (
    <div className="flex flex-col gap-4">
      <Card className={cn('min-w-0', !balanced && 'border border-warn/40 bg-warn-soft/30')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[17px] font-semibold text-ink">
              {showHeader ? section.name : 'Bobot indikator'}
            </h2>
            <p className="mt-0.5 text-[13px] text-ink-secondary">
              {showHeader && (
                <>
                  Dinilai per {section.subject === 'tbm' ? 'TBM' : 'individu PELITA SLI'} ·{' '}
                  {Math.round(section.weightInFinal * 100)}% dari nilai akhir ·{' '}
                </>
              )}
              Σ seluruh bobot harus tepat 100.
            </p>
          </div>

          <span
            className={cn(
              'tabular rounded-full px-3.5 py-1.5 text-sm font-semibold',
              balanced ? 'bg-success-soft text-success' : 'bg-warn-soft text-[#9a6400]',
            )}
          >
            Σ bobot {round2(total)}
            {balanced ? ' ✓' : ' — belum 100'}
          </span>
        </div>
      </Card>

      {section.dimensions.map((dimension) => (
        <DimensionTable
          key={dimension.id}
          dimension={dimension}
          canEdit={canEdit}
          evidenceLabel={evidenceLabel}
          onEdit={() => onEditDimension(dimension)}
          onDelete={() => onDeleteDimension(dimension)}
          onAddIndicator={() => onAddIndicator(dimension)}
          onEditIndicator={(i) => onEditIndicator(dimension, i)}
          onDeleteIndicator={onDeleteIndicator}
        />
      ))}

      {canEdit && (
        <div>
          <Button variant="secondary" onClick={onAddDimension}>
            + Dimensi Baru
          </Button>
        </div>
      )}
    </div>
  );
}

function DimensionTable({
  dimension,
  canEdit,
  evidenceLabel,
  onEdit,
  onDelete,
  onAddIndicator,
  onEditIndicator,
  onDeleteIndicator,
}: {
  dimension: Dimension;
  canEdit: boolean;
  evidenceLabel: string;
  onEdit: () => void;
  onDelete: () => void;
  onAddIndicator: () => void;
  onEditIndicator: (i: Indicator) => void;
  onDeleteIndicator: (i: Indicator) => void;
}) {
  const weight = dimension.indicators.reduce((s, i) => s + i.weight, 0);

  return (
    <Card padded={false} className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div className="min-w-0">
          <h3 className="font-semibold text-ink">
            <span className="tabular text-ink-muted">{dimension.code}</span> {dimension.name}
          </h3>
          {dimension.journey && (
            <p className="mt-0.5 text-[13px] text-ink-secondary">{dimension.journey}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="tabular rounded-full bg-app px-3 py-1 text-sm font-semibold text-ink">
            bobot {round2(weight)}
          </span>
          {canEdit && (
            <>
              <Button size="sm" variant="ghost" onClick={onEdit}>
                Ubah
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete}>
                Hapus
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabel selebar Excel-nya. overflow-x-auto + min-w-0 pada Card supaya
          yang tergeser hanya tabelnya, bukan seluruh halaman. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] border-collapse text-left">
          <thead>
            <tr className="border-b border-border">
              <Th className="w-12">No.</Th>
              <Th className="w-56">Indikator</Th>
              <Th className="w-64">Deskripsi</Th>
              <Th className="w-48">Kriteria 1</Th>
              <Th className="w-48">Kriteria 2</Th>
              <Th className="w-48">Kriteria 3</Th>
              <Th className="w-48">Kriteria 4</Th>
              <Th className="w-20">Bobot</Th>
              <Th className="w-48">{evidenceLabel}</Th>
              {canEdit && <Th className="w-28">Aksi</Th>}
            </tr>
          </thead>
          <tbody>
            {dimension.indicators.map((indicator) => (
              <tr key={indicator.id} className="border-b border-border align-top last:border-0">
                <Td>
                  <span className="tabular text-ink-secondary">{indicator.number}</span>
                </Td>
                <Td>
                  <span className="font-medium text-ink">{indicator.name}</span>
                  <span className="mt-1 flex flex-wrap gap-1">
                    {indicator.priorityYear && (
                      <span className="rounded-full bg-primary-soft px-2 py-0.5 text-[12px] font-medium text-primary">
                        prioritas Th {indicator.priorityYear}
                      </span>
                    )}
                    {indicator.needsVerification && (
                      <span className="rounded-full bg-warn-soft px-2 py-0.5 text-[12px] font-medium text-[#9a6400]">
                        ⚠ perlu konfirmasi
                      </span>
                    )}
                  </span>
                </Td>
                <Td>
                  <Clamp text={indicator.description} />
                </Td>
                {[1, 2, 3, 4].map((score) => (
                  <Td key={score}>
                    <Clamp
                      text={indicator.criteria.find((c) => c.score === score)?.description ?? null}
                    />
                  </Td>
                ))}
                <Td>
                  <span className="tabular font-semibold text-ink">{indicator.weight}</span>
                </Td>
                <Td>
                  {indicator.mov.length === 0 ? (
                    <span className="text-[13px] text-ink-muted">—</span>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {indicator.mov.map((m, i) => (
                        <li key={i} className="text-[13px] text-ink">
                          • {m.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </Td>
                {canEdit && (
                  <Td>
                    <div className="flex flex-col gap-1">
                      <Button size="sm" variant="ghost" onClick={() => onEditIndicator(indicator)}>
                        Ubah
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDeleteIndicator(indicator)}>
                        Hapus
                      </Button>
                    </div>
                  </Td>
                )}
              </tr>
            ))}

            {dimension.indicators.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 10 : 9} className="px-4 py-6 text-center text-ink-secondary">
                  Belum ada indikator di dimensi ini.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div className="border-t border-border px-5 py-3">
          <Button size="sm" variant="secondary" onClick={onAddIndicator}>
            + Indikator
          </Button>
        </div>
      )}
    </Card>
  );
}

function DimensionEditor({
  dimension,
  saving,
  error,
  onCancel,
  onSave,
}: {
  dimension: Dimension | null;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: { code: string; name: string }) => void;
}) {
  const [code, setCode] = useState(dimension?.code ?? '');
  const [name, setName] = useState(dimension?.name ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!code.trim()) errors.code = 'Kode wajib diisi';
    if (!name.trim()) errors.name = 'Nama wajib diisi';
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    onSave({ code: code.trim(), name: name.trim() });
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4"
    >
      <div className="w-full max-w-md rounded-t-[var(--radius-card)] bg-surface p-5 shadow-[var(--shadow-hover)] sm:rounded-[var(--radius-card)]">
        <h2 className="text-[19px] font-semibold text-ink">
          {dimension ? 'Ubah Dimensi' : 'Dimensi Baru'}
        </h2>

        {error && (
          <div className="mt-3">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}

        <div className="mt-4 flex flex-col gap-4">
          <Input
            label="Kode"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={fieldErrors.code}
            hint="Mis. D1, K1"
            required
          />
          <Input
            label="Nama dimensi"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={fieldErrors.name}
            required
          />
        </div>

        <p className="mt-4 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 text-[13px] text-ink-secondary">
          Bobot dimensi tidak diisi di sini — nilainya adalah jumlah bobot indikator di dalamnya,
          supaya keduanya mustahil berbeda.
        </p>

        <div className="mt-5 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            Batal
          </Button>
          <Button type="submit" loading={saving}>
            Simpan
          </Button>
        </div>
      </div>
    </form>
  );
}

/** Teks panjang dipangkas jadi 3 baris agar tinggi baris tabel tetap terkendali. */
function Clamp({ text }: { text: string | null }) {
  if (!text) return <span className="text-[13px] text-ink-muted">—</span>;
  return (
    <span className="line-clamp-3 text-[13px] text-ink" title={text}>
      {text}
    </span>
  );
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-3 text-[13px] font-semibold text-ink-secondary', className)}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3.5 align-top">{children}</td>;
}
